import { useTranslation } from "react-i18next";
import Header from "../header";
import styles from "./home.module.scss";
import { useState, useMemo, useCallback, useEffect } from "react";
import Loading from "../../../components/common/loading";
import useGetContractStats from "../../../hooks/useGetContractStats";
import useGetUserStats from "../../../hooks/useGetUserStats";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { ROUTES } from "../../../setup/constants";
import { useNavigate } from "react-router-dom";
import { getExternalContracts } from "../../../setup/api/services/contract";
import ContractsListItem from "../contractListItem";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import Spinner from "../../../components/common/Spinner";
import { formatBigNumber } from "../../../setup/util/formatBigNumber";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";

const BusinessHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userToken = AuthConfig.getToken();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const clientAdminUserName = AuthConfig.getUserInfo()?.profile?.fullName;
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );

  const organizationCode = useOrganizationCode(userToken) || "";

  const { loading: usersStatsLoading, data: usersStats } =
    useGetUserStats(organizationCode);
  const { loading: contractStatsLoading, data: contractStats } =
    useGetContractStats(undefined, organizationCode);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        listType: "profile",
        page: 0,
        size: 3,
        organizationCode,
        type: EContractType.COMPLEX_CONTRACT,
      };
      const response = await getExternalContracts(body);
      setContracts(response?.data?.content);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [organizationCode]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  if (usersStatsLoading || contractStatsLoading)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Spinner />
      </div>
    );

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.topBar}>
        <div className={styles.leftCol}>
          <h5 className={styles.welcomeTitle}>
            {t("lbl_welcome_user", {
              val: clientAdminUserName,
            })}
          </h5>
          <p className={styles.welcomeSubtitle}>
            {t("lbl_business_dashboard_subtitle")}
          </p>
        </div>
      </div>
      <div className={styles.statsRow}>
        <div className={styles.statsCards}>
          <div className={styles.card}>
            <img
              src="/assets/images/svg/transactions.svg"
              alt={t("lbl_transactions")}
              className={styles.cardIcon}
            />
            <div className={styles.cardDetails}>
              <p className={styles.cardSubtitle}>
                {t("lbl_total_transactions_count")}
              </p>
              <h5 className={styles.cardValue}>
                {Object.values(
                  contractStats as { [key: string]: { count: number } }
                ).reduce((sum, val) => sum + val?.count, 0)}
              </h5>
            </div>
          </div>
          <div className={styles.card}>
            <img
              src="/assets/images/svg/clients.svg"
              alt={t("lbl_transactions")}
              className={styles.cardIcon}
            />
            <div className={styles.cardDetails}>
              <p className={styles.cardSubtitle}>{t("lbl_customers_count")}</p>
              <h5 className={styles.cardValue}>
                {usersStats?.totalUsersCount}
              </h5>
            </div>
          </div>
          <div className={styles.card}>
            <img
              src="/assets/images/svg/amounts.svg"
              alt={t("lbl_transactions")}
              className={styles.cardIcon}
            />
            <div className={styles.cardDetails}>
              <p className={styles.cardSubtitle}>
                {t("lbl_total_transactions_amount")}
              </p>
              <h5 className={styles.cardValue}>
                {`${formatBigNumber(
                  Object.values(
                    contractStats as { [key: string]: { totalAmount: number } }
                  ).reduce((sum, val) => sum + val?.totalAmount, 0)
                )} ${t("lbl_saudi_currency")}`}
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderContent}>
          <h4 className={styles.sectionTitle}>{t("lbl_projects")}</h4>
          <p
            onClick={() => navigate(ROUTES.CONTRACTS_LIST)}
            className={styles.link}>
            {t("lbl_view_all")}
          </p>
        </div>
      </div>
      {contracts?.length === 0 ? (
        <>
          {!loading && (
            <div className={styles.emptyWrapper}>
              <div className={styles.emptyContent}>
                <img
                  src="/assets/images/svg/emptyState.svg"
                  alt={t("lbl_no_transactions")}
                  className={styles.emptyIcon}
                />
                <div className={styles.emptyText}>
                  <h5 className={styles.emptyTitle}>
                    {t("lbl_no_projects_yet")}
                  </h5>
                  <p className={styles.emptySubtitle}>
                    {t("lbl_create_new_project_now")}
                  </p>
                </div>
                {(authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
                  authorities?.includes("CLIENT_ADMIN") ||
                  authorities?.includes("ORGANIZATION_OWNER")) && (
                  <p
                    onClick={() =>
                      navigate(
                        ROUTES.CREATE_COMPLEX_CONTRACT.replace(
                          ":complexId?/",
                          ""
                        ).replace(":milestoneId?", "")
                      )
                    }
                    className={styles.emptyCta}>
                    {t("lbl_create_new_project")}
                  </p>
                )}
              </div>
            </div>
          )}
          {loading && (
            <div className={styles.loadingContainer}>
              <Spinner />
            </div>
          )}
        </>
      ) : authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
        authorities?.includes("CLIENT_ADMIN") ||
        authorities?.includes("ORGANIZATION_OWNER") ? (
        <div className={styles.contractsContainer}>
          {contracts?.map((contract) => (
            <ContractsListItem
              key={(contract as { id: string })?.id}
              title={contract?.itemDetail?.title}
              description={contract?.itemDetail?.description}
              shortId={contract?.shortId}
              status={contract?.status}
              onClick={() =>
                navigate(
                  ROUTES.CONTRACT_DETAILS?.replace(":id", contract?.id),
                  { state: { from: location.pathname } }
                )
              }
              milestoneCount={contract?.activeChildrenCount}
              partiesCount={contract?.activeChildrenPartiesCount}
              itemPrice={contract?.activeChildrenTotalAmount}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default BusinessHome;
